import React from 'react';
import styled, { css } from 'styled-components';
import { Link } from 'wouter';
import { ApiClient } from '../../../api/ApiClient';
import { v2 } from '../../../api/ApiInterfaceV2';
import { useObserverReturningStore } from '../../../CodesThatsGottaGoSomewhereElse';
import { Icon } from '../../../concepts/resources/Icon';
import { useLoader } from '../../../platform/Hook';
import { Colors } from '../../../ui';
import { Badge } from '../../../ui/Badge';
import { DropButton } from '../../../ui/DropButton';
import { Position } from '../../../ui/Position';
import { AppLocation } from '../../AppLocation';
import { ShowIfHasFeature } from '../../common/FeatureElements';
import { useResolve } from '../../Container';
import { AccountModel } from '../AccountModel';

const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: .5rem;
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    padding: 0rem 0.5rem .375rem 1.7rem;

    ${DropButton} {
        margin-left: auto;
        margin-right: .5rem;

        &:hover {
            color: ${Colors.primaryText}
        }
    }
`;

const ItemDisc = styled.div<{ active?: boolean }>`
    display: inline-block;
    width: .5em;
    height: .5em;
    border: solid 2px #fff;
    border-radius: 50%;
    margin: 0 .4375rem;

    ${p => p.active ? 'background: white;' : ''}
`;

const ItemLabel = styled.span`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const ItemIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.25rem;
    height: 2.25rem;
    border-radius: .25rem;
    
    svg {
        width: 1.75rem;
        height: 1.75rem;
    }
`;

const ItemPost = styled.div`
    display: inline-block;
    height: 1em;
    svg {
        display: inline-block;
    }
`;

const Item = styled.div<{ active?: boolean }>`
    position: relative;
    display: flex;
    align-items: center;
    gap: .5rem;
    border-radius: .25rem;
    cursor: pointer;
    user-select: none;
    font-weight: 420;
    color: ${Colors.text};
 
    ${p => p.active && css`
        color: ${Colors.primary};
        ${ItemIcon} {
            background: rgba(59, 101, 239, 0.20);
        }
    `}
    &:hover {
        color: ${Colors.primary};
        ${ItemIcon} {
            background: rgba(59, 101, 239, 0.10);
        }
    }
    &:active {
        color: ${Colors.primary};
        ${ItemIcon} {
            background: rgba(59, 101, 239, 0.20);
        }
    }

    svg, ${ItemDisc} {
        /* position: absolute;
        left: 2.25rem;
        transform: translateX(-50%); */
    }

    ${ItemDisc} {
        transform: translateX(-50%) translateX(-.1rem);
    }
`;

const WorkspaceLimitWarning = () => {

    const { plan } = useResolve(AccountModel);
    const api = useResolve(ApiClient);

    const limit = plan.features.workspaceLimit;
    if (!limit) return null;

    const [loading, total] = useLoader(async () => api.exec(v2.admin.workspaces.list()).then(r => r.length), []);;

    if (loading) return null;
    if (total <= limit) return null;

    return <Position at="right">
        <Badge size="lg" color={Colors.observe}>
            <Icon size="1.25" src="deqo://icons/hero-mini/exclamation-triangle" />
        </Badge>
    </Position>;
};

const UserLimitWarning = () => {

    const { plan } = useResolve(AccountModel);
    const api = useResolve(ApiClient);

    const metric = plan.features.quantityMetric;
    const comps = Number(plan.features.quantityExtra ?? 0);
    const limit = (metric === 'workspace' ? plan.quantity * 20 : plan.quantity) + comps;

    const [loading, total] = useLoader(() => api.exec(v2.admin.users.stats()).then(r => r.nonGuests), []);

    if (loading) return null;
    if (total <= limit) return null;

    return <Position at="right">
        <Badge size="lg" color={Colors.observe}>
            <Icon size="1.25" src="deqo://icons/hero-mini/exclamation-triangle" />
        </Badge>
    </Position>;
};

export const AdminMenu = () => {

    const account = useResolve(AccountModel);
    const location = useObserverReturningStore(useResolve(AppLocation));

    return <>
        <Section>
            <Link to="~/">
                <Item>
                    <ItemIcon><Icon src="deqo://icons/hero/arrow-circle-left" /></ItemIcon>
                    <ItemLabel>Back To Dashboard</ItemLabel>
                </Item>
            </Link>
        </Section>
        <Section>
            <Link to="~/manage/info">
                <Item active={location.includes('info')}>
                    <ItemIcon><Icon src="deqo://icons/dash/admin-info" /></ItemIcon>
                    <ItemLabel>Overview</ItemLabel>
                </Item>
            </Link>
            <Link to="~/manage/people">
                <Item active={location.includes('people')}>
                    <ItemIcon><Icon src="deqo://icons/dash/admin-people" /></ItemIcon>
                    <ItemLabel>People</ItemLabel>
                    <UserLimitWarning />
                </Item>
            </Link>
            <Link to="~/manage/teams">
                <Item active={location.includes('teams')}>
                    <ItemIcon><Icon src="deqo://icons/dash/admin-teams" /></ItemIcon>
                    <ItemLabel>Teams</ItemLabel>
                    <WorkspaceLimitWarning />
                </Item>
            </Link>
            <Link to="~/manage/boards">
                <Item active={location.includes('boards')}>
                    <ItemIcon><Icon src="deqo://icons/dash/boards" /></ItemIcon>
                    <ItemLabel>Boards</ItemLabel>
                </Item>
            </Link>
            {account.plan?.features?.assistant &&
                <Link to="~/manage/ai">
                    <Item active={location.includes('ai')}>
                        <ItemIcon><Icon src="deqo://icons/hero-mini/beaker" /></ItemIcon>
                        <ItemLabel>AI</ItemLabel>
                    </Item>
                </Link>
            }
            <Link to="~/manage/access">
                <Item active={location.includes('access')}>
                    <ItemIcon><Icon src="deqo://icons/dash/admin-access" /></ItemIcon>
                    <ItemLabel>Access Control</ItemLabel>
                </Item>
            </Link>
            <Link to="~/manage/sso">
                <Item active={location.includes('sso')}>
                    <ItemIcon><Icon src="deqo://icons/dash/admin-sso" /></ItemIcon>
                    <ItemLabel>Single Sign-On</ItemLabel>
                </Item>
            </Link>
            <ShowIfHasFeature name="activityLog">
                <Link to="~/manage/activity">
                    <Item active={location.includes('activity-logs')}>
                        <ItemIcon><Icon src="deqo://icons/dash/list-view" /></ItemIcon>
                        <ItemLabel>Activity Logs</ItemLabel>
                    </Item>
                </Link>
            </ShowIfHasFeature>
            <Link to="~/manage/danger">
                <Item active={location.includes('danger')}>
                    <ItemIcon><Icon src="deqo://icons/dash/admin-danger" /></ItemIcon>
                    <ItemLabel>Danger Zone</ItemLabel>
                </Item>
            </Link>
        </Section>
        <Section>
            {account.metadata?.useLegacyBilling ? (
                <Link to="~/manage/billing">
                    <Item active={location.includes('billing')}>
                        <ItemIcon><Icon src="deqo://icons/dash/admin-billing" /></ItemIcon>
                        <ItemLabel>Billing</ItemLabel>
                    </Item>
                </Link>
            ) : (
                <a target="_blank" href={`/billing?accountId=${account.id}`} rel="noreferrer">
                    <Item>
                        <ItemIcon><Icon src="deqo://icons/dash/admin-billing" /></ItemIcon>
                        <ItemLabel>
                            Billing
                        </ItemLabel>
                        <ItemPost>
                            <Icon size="1" src="deqo://icons/hero/external-link" />
                        </ItemPost>
                    </Item>
                </a>
            )}
        </Section>
    </>;
};

